<template>
  <div class="space-y-6">
    <div class="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <div class="text-sm font-semibold text-slate-700">Permanent users</div>
        <h2 class="mt-1 text-2xl font-bold tracking-tight text-slate-900">Monthly subscribers</h2>
      </div>
      <button
        class="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sky-500"
        @click="openCreate"
      >
        + Add permanent user
      </button>
    </div>

    <div class="rounded-2xl bg-white p-4 ring-1 ring-slate-200">
      <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex-1">
          <input
            v-model.trim="q"
            placeholder="Search by plate, name, phone…"
            class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none ring-sky-200 focus:ring-4"
          />
        </div>
        <div class="flex items-center gap-2 text-xs">
          <span class="rounded-full bg-rose-50 px-3 py-1 font-semibold text-rose-700 ring-1 ring-rose-200">
            {{ expired.length }} expired
          </span>
          <span class="rounded-full bg-emerald-50 px-3 py-1 font-semibold text-emerald-700 ring-1 ring-emerald-200">
            {{ active.length }} active
          </span>
        </div>
      </div>

      <div class="mt-4 overflow-x-auto rounded-xl ring-1 ring-slate-200">
        <table class="w-full min-w-[820px] text-left text-xs">
          <thead class="bg-slate-50 text-slate-600">
            <tr>
              <th class="px-3 py-2 font-semibold">Name</th>
              <th class="px-3 py-2 font-semibold">Phone</th>
              <th class="px-3 py-2 font-semibold">Plate</th>
              <th class="px-3 py-2 font-semibold">Subscription</th>
              <th class="px-3 py-2 font-semibold">
                <button
                  type="button"
                  class="inline-flex items-center gap-1.5 rounded-md px-1 py-0.5 -mx-1 text-left font-semibold text-slate-600 hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
                  :aria-sort="statusSortDir === 'asc' ? 'ascending' : statusSortDir === 'desc' ? 'descending' : 'none'"
                  @click="toggleStatusSort"
                >
                  Status
                  <span class="inline-flex flex-col text-[9px] leading-[0.55]" aria-hidden="true">
                    <span :class="statusSortDir === 'asc' ? 'text-sky-600' : 'text-slate-300'">▲</span>
                    <span :class="statusSortDir === 'desc' ? 'text-sky-600' : 'text-slate-300'">▼</span>
                  </span>
                </button>
              </th>
              <th class="px-3 py-2 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in filtered" :key="c.id" class="border-t border-slate-200">
              <td class="px-3 py-2 font-semibold text-slate-900">{{ c.name }}</td>
              <td class="px-3 py-2 text-slate-600">{{ c.phone }}</td>
              <td class="px-3 py-2 font-mono text-slate-900">{{ c.plateNumber }}</td>
              <td class="px-3 py-2 text-slate-600">
                {{ d(c.subscriptionStart) }} → {{ d(c.subscriptionEnd) }}
              </td>
              <td class="px-3 py-2">
                <span
                  class="rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1"
                  :class="
                    isExpired(c) || c.status === 'inactive'
                      ? 'bg-rose-50 text-rose-700 ring-rose-200'
                      : 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                  "
                >
                  {{ isExpired(c) ? "expired" : c.status }}
                </span>
              </td>
              <td class="px-3 py-2 text-right">
                <button class="rounded-lg border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50" @click="openEdit(c)">
                  Edit
                </button>
                <button
                  class="ml-2 rounded-lg border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  @click="toggleActive(c)"
                >
                  {{ c.status === "active" ? "Deactivate" : "Activate" }}
                </button>
              </td>
            </tr>
            <tr v-if="filtered.length === 0">
              <td class="px-3 py-8 text-center text-slate-500" colspan="6">No permanent users found.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="modalOpen" class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div class="w-full max-w-lg rounded-2xl bg-white p-5 shadow-lg ring-1 ring-slate-200">
        <div class="flex items-start justify-between">
          <div>
            <div class="text-sm font-semibold text-slate-700">{{ editingId ? "Edit" : "Create" }}</div>
            <div class="mt-1 text-xl font-bold text-slate-900">Permanent user</div>
          </div>
          <button class="rounded-lg px-2 py-1 text-sm text-slate-600 hover:bg-slate-50" @click="close">✕</button>
        </div>

        <form class="mt-4 grid gap-4 sm:grid-cols-2" @submit.prevent="save">
          <div class="sm:col-span-2">
            <label class="text-xs font-semibold text-slate-700">Full name</label>
            <input v-model.trim="form.name" class="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none ring-sky-200 focus:ring-4" />
          </div>
          <div>
            <label class="text-xs font-semibold text-slate-700">Phone</label>
            <input v-model.trim="form.phone" class="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none ring-sky-200 focus:ring-4" />
          </div>
          <div>
            <label class="text-xs font-semibold text-slate-700">Plate number</label>
            <input v-model.trim="form.plateNumber" class="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono outline-none ring-sky-200 focus:ring-4" />
          </div>
          <div>
            <label class="text-xs font-semibold text-slate-700">Subscription start</label>
            <input v-model="form.subscriptionStart" type="date" class="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none ring-sky-200 focus:ring-4" />
          </div>
          <div>
            <label class="text-xs font-semibold text-slate-700">Subscription end</label>
            <input v-model="form.subscriptionEnd" type="date" class="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none ring-sky-200 focus:ring-4" />
          </div>

          <div class="sm:col-span-2 flex items-center justify-end gap-2 pt-2">
            <button type="button" class="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" @click="close">
              Cancel
            </button>
            <button type="submit" class="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sky-500">
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref } from "vue";
import { useStore } from "vuex";
import { toast } from "@/toast";

const store = useStore();
const q = ref("");
const modalOpen = ref(false);
const editingId = ref("");
/** null = preserve list order; asc/desc = sort by effective status */
const statusSortDir = ref(null);

const form = reactive({
  name: "",
  phone: "",
  plateNumber: "",
  subscriptionStart: "",
  subscriptionEnd: "",
});

const customers = computed(() => store.state.permanentCustomers);
const expired = computed(() => store.getters.expiredPermanentCustomers);
const active = computed(() => customers.value.filter((c) => c.status === "active" && !isExpired(c)));

const searchFiltered = computed(() => {
  const query = q.value.toLowerCase();
  if (!query) return customers.value;
  return customers.value.filter((c) => {
    const blob = `${c.name} ${c.phone} ${c.plateNumber}`.toLowerCase();
    return blob.includes(query);
  });
});

/** Matches Status column groups: expired (date) → inactive → active */
function statusRank(c) {
  if (isExpired(c)) return 2;
  if (c.status === "inactive") return 1;
  return 0;
}

const filtered = computed(() => {
  const list = searchFiltered.value.slice();
  const dir = statusSortDir.value;
  if (!dir) return list;
  const mul = dir === "asc" ? 1 : -1;
  list.sort((a, b) => {
    const cmp = statusRank(a) - statusRank(b);
    if (cmp !== 0) return cmp * mul;
    return String(a.plateNumber || "").localeCompare(String(b.plateNumber || ""));
  });
  return list;
});

function toggleStatusSort() {
  if (statusSortDir.value === null) statusSortDir.value = "asc";
  else if (statusSortDir.value === "asc") statusSortDir.value = "desc";
  else statusSortDir.value = "asc";
}

function isExpired(c) {
  const end = new Date(c.subscriptionEnd).getTime();
  return Number.isFinite(end) && end < Date.now();
}

function openCreate() {
  editingId.value = "";
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  form.name = "";
  form.phone = "";
  form.plateNumber = "";
  form.subscriptionStart = start.toISOString().slice(0, 10);
  form.subscriptionEnd = end.toISOString().slice(0, 10);
  modalOpen.value = true;
}

function openEdit(c) {
  editingId.value = c.id;
  form.name = c.name || "";
  form.phone = c.phone || "";
  form.plateNumber = c.plateNumber || "";
  form.subscriptionStart = String(c.subscriptionStart || "").slice(0, 10);
  form.subscriptionEnd = String(c.subscriptionEnd || "").slice(0, 10);
  modalOpen.value = true;
}

function close() {
  modalOpen.value = false;
}

function save() {
  if (!form.name.trim()) return toast.error("Name is required.");
  if (!form.plateNumber.trim()) return toast.error("Plate number is required.");
  if (!form.subscriptionStart || !form.subscriptionEnd) return toast.error("Subscription dates are required.");

  store.commit("upsertPermanentCustomer", {
    id: editingId.value || undefined,
    name: form.name,
    phone: form.phone,
    plateNumber: form.plateNumber,
    subscriptionStart: form.subscriptionStart,
    subscriptionEnd: form.subscriptionEnd,
    status: "active",
  });
  store.commit("log", {
    action: editingId.value ? "update_permanent" : "create_permanent",
    actorId: store.getters.authedUser?.id,
    actorRole: "admin",
    message: `Permanent user saved: ${form.plateNumber}`,
  });
  toast.success("Saved.");
  close();
}

function toggleActive(c) {
  const next = c.status === "active" ? "inactive" : "active";
  store.commit("setPermanentStatus", { id: c.id, status: next });
  store.commit("log", {
    action: "toggle_permanent_status",
    actorId: store.getters.authedUser?.id,
    actorRole: "admin",
    message: `Permanent user ${c.plateNumber} set to ${next}.`,
  });
  toast.info(`Status: ${next}`);
}

function d(v) {
  try {
    return new Date(v).toLocaleDateString();
  } catch {
    return v;
  }
}
</script>

